import React, { useMemo } from 'react';
import { Select, SelectProps } from 'antd';

// Утилиты для умного поиска
const normalizeCompanyName = (name: string): string => {
  if (!name) return '';
  
  // Убираем префиксы компаний и приводим к нижнему регистру
  const prefixes = [
    'ооо', 'оао', 'ао', 'пао', 'ип', 'зао', 'оао', 'пao', 'тoo', 'нaо', 'кoo',
    'общество с ограниченной ответственностью',
    'открытое акционерное общество',
    'акционерное общество',
    'публичное акционерное общество',
    'индивидуальный предприниматель',
    'закрытое акционерное общество'
  ];
  
  let normalized = name.toLowerCase().trim();
  
  // Убираем префиксы
  for (const prefix of prefixes) {
    if (normalized.startsWith(prefix + ' ')) {
      normalized = normalized.substring(prefix.length + 1).trim();
      break;
    }
    if (normalized.startsWith(prefix + '«')) {
      normalized = normalized.substring(prefix.length + 1).trim();
      break;
    }
    if (normalized.startsWith(prefix + '"')) {
      normalized = normalized.substring(prefix.length + 1).trim();
      break;
    }
  }
  
  // Убираем кавычки
  normalized = normalized.replace(/[«»""]/g, '').trim();
  
  return normalized;
};

const normalizeSearchQuery = (query: string): string => {
  return query.toLowerCase().trim().replace(/[«»""]/g, '');
};

interface SmartSelectProps extends Omit<SelectProps, 'filterOption' | 'optionFilterProp' | 'options'> {
  options: Array<{
    value: any;
    label: string;
    [key: string]: any;
  }>;
  searchFields?: string[]; // Поля для поиска (по умолчанию ['label'])
  sortBy?: 'alphabetical' | 'none'; // Сортировка (по умолчанию 'alphabetical')
  normalizeSearch?: boolean; // Нормализация поиска для компаний (по умолчанию true)
}

const SmartSelect: React.FC<SmartSelectProps> = ({
  options = [],
  searchFields = ['label'],
  sortBy = 'alphabetical',
  normalizeSearch = true,
  ...props
}) => {
  // Сортируем опции
  const sortedOptions = useMemo(() => {
    if (sortBy === 'none') return options;
    
    return [...options].sort((a, b) => {
      const labelA = normalizeSearch ? normalizeCompanyName(a.label) : a.label.toLowerCase();
      const labelB = normalizeSearch ? normalizeCompanyName(b.label) : b.label.toLowerCase();
      return labelA.localeCompare(labelB, 'ru');
    });
  }, [options, sortBy, normalizeSearch]);

  // Функция фильтрации
  const filterOption = (input: string, option: any): boolean => {
    if (!input) return true;
    
    const searchQuery = normalizeSearch ? normalizeSearchQuery(input) : input.toLowerCase();
    
    // 1) Пробуем искать по указанным полям (если Select.options прокинул исходные поля)
    for (const field of searchFields) {
      const fieldValue = (option && option[field]) ?? (option?.data && option.data[field]);
      if (fieldValue) {
        const valueStr = String(fieldValue);
        const normalizedFieldValue = normalizeSearch
          ? normalizeCompanyName(valueStr)
          : valueStr.toLowerCase();
        if (normalizedFieldValue.includes(searchQuery)) {
          return true;
        }
      }
    }

    // 2) Фолбэк — ищем по label
    const labelValue = option?.label ?? option?.children;
    if (labelValue) {
      const labelStr = String(labelValue);
      const normalized = normalizeSearch ? normalizeCompanyName(labelStr) : labelStr.toLowerCase();
      if (normalized.includes(searchQuery)) {
        return true;
      }
    }

    return false;
  };

  return (
    <Select
      {...props}
      filterOption={filterOption}
      showSearch
      optionFilterProp="label"
      options={sortedOptions}
    >
    </Select>
  );
};

export default SmartSelect;
